import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Copy, Download, KeyRound, Monitor, Pencil, Plus, Printer, RefreshCw, Send, ShieldOff } from 'lucide-react';
import type { Permission } from '@garlic/shared';
import { api, errorMessage, fieldErrors, patch, post } from '../../lib/api';
import { formatDateTime, relativeTime } from '../../lib/format';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, Field, Input, PageHeader, Select, Spinner } from '../../ui/primitives';
import { Dialog, useToast } from '../../ui/feedback';
import { Switch } from '../../ui/controls';
import type { TenantRestaurant } from '../TenantApp';

type Agent = { id: string; name: string; hostname: string | null; version: string | null; lastSeenAt: string | null; pairedAt: string | null; online: boolean; pairingPending: boolean };
type PrinterRow = {
  id: string; name: string; stationId: string | null; agentId: string | null; connection: 'NETWORK' | 'WINDOWS' | 'DEVICE'; address: string; columns: 32 | 42 | 48; encoding: 'cp860' | 'ascii';
  copies: number; active: boolean; lastPrintedAt: string | null; lastError: string | null; lastErrorAt: string | null; station: { name: string } | null; agent: { name: string } | null;
};
type Job = { id: string; kind: string; status: string; attempts: number; lastError: string | null; createdAt: string; printedAt: string | null; printerName: string; orderNumber: number | null; tableLabel: string | null; stationName: string };
type Data = { enabled: boolean; agents: Agent[]; printers: PrinterRow[]; stations: { id: string; name: string }[]; jobs: Job[] };

const STATUS: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' | 'brand' }> = {
  PENDING: { label: 'Na fila', tone: 'brand' }, SENT: { label: 'Enviado', tone: 'warning' }, PRINTED: { label: 'Impresso', tone: 'success' }, FAILED: { label: 'Falhou', tone: 'danger' }, CANCELLED: { label: 'Cancelado', tone: 'neutral' },
};
const KIND: Record<string, string> = { TICKET: 'Comanda', REPRINT: 'Reimpressão', CANCEL: 'Cancelamento', TEST: 'Teste' };

export function StaffPrinting({ restaurant, permissions }: { restaurant: TenantRestaurant; permissions: Permission[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['staff-printing'], queryFn: () => api<Data>('/api/staff/printing'), refetchInterval: 20_000 });
  const [pairing, setPairing] = useState<{ code: string; url: string } | null>(null);
  const [newAgent, setNewAgent] = useState(false);
  const [printer, setPrinter] = useState<PrinterRow | 'new' | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['staff-printing'] });
  const run = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      await refresh();
      toast.success(msg);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  if (q.isPending) return <Spinner />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  const d = q.data;

  return (
    <>
      <PageHeader title="Impressão" description="Comandas saem sozinhas nas térmicas de cada setor. Se algo falhar, o pedido continua na tela da cozinha/bar." />
      {!d.enabled && <div className="mb-6"><Alert tone="warning">A impressão automática não está habilitada no seu plano. Fale com o Garlic.</Alert></div>}
      <div className="space-y-6">
        <AgentDownload />
        <Card>
          <CardHeader
            title="Agentes de impressão"
            description="Programa instalado num computador do restaurante (Windows ou Linux) ligado às impressoras. Só faz conexões de saída — não é preciso abrir portas."
            action={<Button icon={<Plus className="size-4" />} onClick={() => setNewAgent(true)}>Novo agente</Button>}
          />
          {d.agents.length === 0 ? (
            <EmptyState icon={<Monitor className="size-5" />} title="Nenhum agente pareado">Crie um agente e use o código no computador das impressoras.</EmptyState>
          ) : (
            <ul className="divide-y divide-line">
              {d.agents.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                  <Monitor className="size-5 text-ink-3" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{a.name}</p>
                    <p className="text-xs text-ink-3">
                      {a.pairedAt ? `${a.hostname ?? 'computador'} · v${a.version ?? '?'} · visto ${a.lastSeenAt ? relativeTime(a.lastSeenAt) : 'nunca'}` : a.pairingPending ? 'Aguardando pareamento' : 'Código expirado — gere outro'}
                    </p>
                  </div>
                  {a.pairedAt && <Badge tone={a.online ? 'success' : 'danger'} dot>{a.online ? 'Online' : 'Offline'}</Badge>}
                  <Button size="sm" variant="ghost" icon={<KeyRound className="size-4" />} onClick={async () => {
                    if (a.pairedAt && !confirm('Gerar novo código? O computador atual precisará ser pareado de novo.')) return;
                    try { setPairing(await post(`/api/staff/printing/agents/${a.id}/pairing`)); await refresh(); } catch (e) { toast.error(errorMessage(e)); }
                  }}>Novo código</Button>
                  <Button size="sm" variant="ghost" icon={<ShieldOff className="size-4" />} onClick={() => confirm(`Revogar "${a.name}"? Ele para de imprimir na hora.`) && run(() => post(`/api/staff/printing/agents/${a.id}/revoke`), 'Agente revogado')}>Revogar</Button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader title="Impressoras" description="Associe cada impressora a um setor e ao agente que a alcança." action={<Button icon={<Plus className="size-4" />} onClick={() => setPrinter('new')} disabled={d.agents.length === 0}>Nova impressora</Button>} />
          {d.printers.length === 0 ? (
            <EmptyState icon={<Printer className="size-5" />} title="Nenhuma impressora">{d.agents.length === 0 ? 'Pareie um agente primeiro.' : 'Cadastre a térmica da cozinha e a do bar.'}</EmptyState>
          ) : (
            <ul className="divide-y divide-line">
              {d.printers.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                  <Printer className="size-5 text-ink-3" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{p.name} {!p.active && <Badge>Inativa</Badge>}</p>
                    <p className="text-xs text-ink-3">
                      {p.station?.name ?? 'Sem setor'} · {p.connection === 'NETWORK' ? 'Rede' : p.connection === 'WINDOWS' ? 'Windows' : 'Dispositivo'} {p.address} · {p.columns === 32 ? '58 mm' : '80 mm'} · agente {p.agent?.name ?? '—'}
                    </p>
                    {p.lastError && <p className="text-xs text-danger-600">Último erro {p.lastErrorAt && relativeTime(p.lastErrorAt)}: {p.lastError}</p>}
                    {!p.lastError && p.lastPrintedAt && <p className="text-xs text-sprout-600">Imprimiu {relativeTime(p.lastPrintedAt)}</p>}
                  </div>
                  <Button size="sm" variant="secondary" icon={<Send className="size-4" />} onClick={() => run(() => post(`/api/staff/printing/printers/${p.id}/test`), 'Teste enviado para a fila')}>Testar</Button>
                  <Button size="sm" variant="ghost" aria-label={`Editar ${p.name}`} icon={<Pencil className="size-4" />} onClick={() => setPrinter(p)} />
                  <Button size="sm" variant="ghost" aria-label={`Remover ${p.name}`} icon={<Archive className="size-4" />} onClick={() => confirm(`Remover "${p.name}"? Trabalhos pendentes dela são cancelados.`) && run(() => post(`/api/staff/printing/printers/${p.id}/archive`), 'Impressora removida')} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader title="Fila e histórico recente" />
          {d.jobs.length === 0 ? <p className="px-5 py-4 text-sm text-ink-3">Nada impresso nas últimas horas.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-ink-3"><tr className="border-b border-line"><th className="px-4 py-2">Quando</th><th className="px-4 py-2">O quê</th><th className="px-4 py-2">Impressora</th><th className="px-4 py-2">Status</th><th className="px-4 py-2" /></tr></thead>
                <tbody className="divide-y divide-line">
                  {d.jobs.map((j) => (
                    <tr key={j.id}>
                      <td className="whitespace-nowrap px-4 py-2 text-ink-3">{formatDateTime(j.createdAt, restaurant.timezone)}</td>
                      <td className="px-4 py-2">{KIND[j.kind] ?? j.kind}{j.orderNumber ? ` #${String(j.orderNumber).padStart(4, '0')}` : ''}{j.tableLabel ? ` · mesa ${j.tableLabel}` : ''}</td>
                      <td className="px-4 py-2">{j.printerName}</td>
                      <td className="px-4 py-2">
                        <Badge tone={STATUS[j.status]?.tone ?? 'neutral'}>{STATUS[j.status]?.label ?? j.status}</Badge>
                        {j.lastError && <span className="block max-w-xs truncate text-xs text-ink-3" title={j.lastError}>{j.lastError} ({j.attempts}×)</span>}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {j.status === 'FAILED' && permissions.includes('printing.reprint') && (
                          <Button size="sm" variant="ghost" icon={<RefreshCw className="size-4" />} onClick={() => run(() => post(`/api/staff/printing/jobs/${j.id}/retry`), 'Reenviado para a fila')}>Reenviar</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {newAgent && <NewAgentDialog onClose={() => setNewAgent(false)} onCreated={(p) => { setPairing(p); void refresh(); }} />}
      {pairing && <PairingDialog pairing={pairing} onClose={() => setPairing(null)} />}
      {printer && <PrinterDialog value={printer} data={d} onClose={() => setPrinter(null)} onSaved={refresh} />}
    </>
  );
}

type PackageInfo = { file: string; agentVersion: string; nodeVersion: string; bytes: number; builtAt: string };
const PACKAGE_URL = '/downloads/garlic-print-agent-windows';

/** Instalador do agente para Windows (gerado no build e servido em /downloads/; traz o Node.js embutido). */
function AgentDownload() {
  const info = useQuery({
    queryKey: ['agent-package'],
    queryFn: async () => {
      const r = await fetch(`${PACKAGE_URL}.json`, { cache: 'no-store' });
      if (!r.ok) throw new Error('indisponível');
      return (await r.json()) as PackageInfo;
    },
    retry: false,
    staleTime: 10 * 60_000,
  });
  return (
    <Card>
      <CardHeader
        title="Instalar no computador do restaurante"
        description="Instale o agente no computador ligado às impressoras (ou na mesma rede delas). Ele liga sozinho junto com o computador."
        action={
          info.data ? (
            <a href={`${PACKAGE_URL}.zip`} download className="inline-flex h-10 items-center gap-2 rounded-xl bg-garlic-600 px-4 text-sm font-semibold text-white hover:bg-garlic-700">
              <Download className="size-4" /> Baixar para Windows
            </a>
          ) : undefined
        }
      />
      <div className="px-5 pb-5 text-sm text-ink-2">
        {info.isError && <Alert tone="warning">O instalador para Windows não está disponível agora. Tente mais tarde ou fale com o Garlic.</Alert>}
        <ol className="list-decimal space-y-1 pl-5">
          <li>Baixe o instalador e extraia o .zip (botão direito → <em>Extrair tudo</em>).</li>
          <li>Dê dois cliques em <strong>Instalar.bat</strong> e confirme a permissão de administrador.</li>
          <li>Aqui, clique em <strong>Novo agente</strong> e cole o <strong>código de instalação</strong> no instalador.</li>
          <li>Cadastre a impressora em <strong>Nova impressora</strong> e clique em <strong>Testar</strong>.</li>
        </ol>
        {info.data && (
          <p className="mt-3 text-xs text-ink-3">
            Versão {info.data.agentVersion} · {(info.data.bytes / 1024 / 1024).toFixed(0)} MB · Windows 10/11 64 bits · não precisa instalar mais nada.
            Se aparecer “O Windows protegeu o computador”, clique em <em>Mais informações → Executar assim mesmo</em>.
          </p>
        )}
      </div>
    </Card>
  );
}

function NewAgentDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (p: { code: string; url: string }) => void }) {
  const toast = useToast();
  const [name, setName] = useState('Computador do caixa');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      onCreated(await post<{ code: string; url: string }>('/api/staff/printing/agents', { name }));
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} title="Novo agente de impressão" footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button type="submit" form="agent-form" loading={busy}>Gerar código</Button></>}>
      <form id="agent-form" onSubmit={submit}>
        <Field label="Nome para identificar o computador">{(id) => <Input id={id} autoFocus value={name} onChange={(e) => setName(e.target.value)} />}</Field>
      </form>
    </Dialog>
  );
}

function PairingDialog({ pairing, onClose }: { pairing: { code: string; url: string }; onClose: () => void }) {
  const toast = useToast();
  // O instalador do Windows reconhece o endereço e o código dentro deste texto.
  const installCode = `${pairing.url} ${pairing.code}`;
  const command = `node agent.js pair ${pairing.url} ${pairing.code}`;
  return (
    <Dialog open onClose={onClose} title="Código de pareamento" footer={<Button onClick={onClose}>Concluído</Button>}>
      <div className="space-y-4 text-sm">
        <p className="text-center font-mono text-4xl font-semibold tracking-widest">{pairing.code}</p>
        <Alert tone="warning">Vale por 15 minutos e uma única vez. Não será exibido de novo.</Alert>
        <div>
          <p className="mb-1 font-medium">Código de instalação: cole no instalador do Windows</p>
          <div className="flex gap-2">
            <Input readOnly value={installCode} className="font-mono text-xs" aria-label="Código de instalação" onFocus={(e) => e.currentTarget.select()} />
            <Button variant="secondary" icon={<Copy className="size-4" />} onClick={() => navigator.clipboard.writeText(installCode).then(() => toast.success('Código copiado'))}>Copiar</Button>
          </div>
          <p className="mt-1 text-xs text-ink-3">Ainda não instalou? Baixe o instalador em <strong>Instalar no computador do restaurante</strong>, nesta página.</p>
        </div>
        <details className="text-xs text-ink-3">
          <summary className="cursor-pointer">Linux ou instalação manual</summary>
          <p className="mt-2">Com Node.js 22+ instalado, rode no computador das impressoras e depois deixe <code className="rounded bg-paper-2 px-1">node agent.js run</code> rodando:</p>
          <div className="mt-2 flex gap-2">
            <Input readOnly value={command} className="font-mono text-xs" aria-label="Comando de pareamento" onFocus={(e) => e.currentTarget.select()} />
            <Button variant="secondary" icon={<Copy className="size-4" />} onClick={() => navigator.clipboard.writeText(command).then(() => toast.success('Comando copiado'))}>Copiar</Button>
          </div>
        </details>
      </div>
    </Dialog>
  );
}

function PrinterDialog({ value, data, onClose, onSaved }: { value: PrinterRow | 'new'; data: Data; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const p = value === 'new' ? null : value;
  const [f, setF] = useState({
    name: p?.name ?? '', stationId: p?.stationId ?? data.stations[0]?.id ?? '', agentId: p?.agentId ?? data.agents.find((a) => a.pairedAt)?.id ?? '',
    connection: p?.connection ?? ('NETWORK' as PrinterRow['connection']), address: p?.address ?? '', columns: p?.columns ?? (48 as PrinterRow['columns']),
    encoding: p?.encoding ?? ('cp860' as PrinterRow['encoding']), copies: p?.copies ?? 1, active: p?.active ?? true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, stationId: f.stationId || null, agentId: f.agentId || null };
      if (p) await patch(`/api/staff/printing/printers/${p.id}`, body);
      else await post('/api/staff/printing/printers', body);
      toast.success('Impressora salva');
      onSaved();
      onClose();
    } catch (err) {
      setErrors(fieldErrors(err));
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const placeholder = { NETWORK: '192.168.0.50:9100', WINDOWS: 'Nome da impressora no Windows (ex.: EPSON TM-T20)', DEVICE: '/dev/usb/lp0' }[f.connection];
  return (
    <Dialog open onClose={onClose} title={p ? `Editar ${p.name}` : 'Nova impressora'} footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button type="submit" form="printer-form" loading={busy}>Salvar</Button></>}>
      <form id="printer-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field className="sm:col-span-2" label="Nome" error={errors.name}>{(id) => <Input id={id} autoFocus placeholder="Ex.: Térmica cozinha" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}</Field>
        <Field label="Setor" error={errors.stationId}>{(id) => <Select id={id} value={f.stationId} onChange={(e) => setF({ ...f, stationId: e.target.value })}><option value="">Nenhum (só testes)</option>{data.stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>}</Field>
        <Field label="Agente" error={errors.agentId}>{(id) => <Select id={id} value={f.agentId} onChange={(e) => setF({ ...f, agentId: e.target.value })}><option value="">—</option>{data.agents.filter((a) => a.pairedAt).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</Select>}</Field>
        <Field label="Conexão">
          {(id) => (
            <Select id={id} value={f.connection} onChange={(e) => setF({ ...f, connection: e.target.value as PrinterRow['connection'] })}>
              <option value="NETWORK">Rede (IP, porta 9100)</option>
              <option value="WINDOWS">USB/instalada no Windows</option>
              <option value="DEVICE">Dispositivo (Linux)</option>
            </Select>
          )}
        </Field>
        <Field label="Endereço" error={errors.address}>{(id) => <Input id={id} placeholder={placeholder} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />}</Field>
        <Field label="Largura do papel">{(id) => <Select id={id} value={f.columns} onChange={(e) => setF({ ...f, columns: Number(e.target.value) as PrinterRow['columns'] })}><option value={48}>80 mm (48 colunas)</option><option value={42}>80 mm (42 colunas)</option><option value={32}>58 mm (32 colunas)</option></Select>}</Field>
        <Field label="Acentuação" hint="Se sair caractere estranho, use “sem acentos”.">{(id, dsc) => <Select id={id} aria-describedby={dsc} value={f.encoding} onChange={(e) => setF({ ...f, encoding: e.target.value as PrinterRow['encoding'] })}><option value="cp860">Português (CP860)</option><option value="ascii">Sem acentos</option></Select>}</Field>
        <Field label="Cópias por comanda">{(id) => <Select id={id} value={f.copies} onChange={(e) => setF({ ...f, copies: Number(e.target.value) })}>{[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}</Select>}</Field>
        <label className="flex items-center justify-between gap-3 self-end rounded-xl border border-line px-3 py-2.5 text-sm">Ativa <Switch checked={f.active} onChange={(active) => setF({ ...f, active })} label="Impressora ativa" /></label>
      </form>
    </Dialog>
  );
}
